import { useLayoutEffect, useState } from 'react';

/**
 * Hook that tracks whether the current page is visible to the user.
 *
 * Returns a boolean that updates whenever the page visibility state changes
 * (e.g., when the user switches tabs or minimizes the browser window).
 *
 * @returns {boolean} `true` if the page is currently visible, `false` otherwise.
 *
 * @example
 * ```ts
 * const isVisible = useIsPageVisible();
 *
 * if (isVisible) {
 *   // Resume animations or API polling
 * } else {
 *   // Pause animations or API polling
 * }
 * ```
 */
export function useIsPageVisible(): boolean {
  const [isVisible, setIsVisible] = useState(document.visibilityState === 'visible');

  const onVisibilityChange = () => {
    setIsVisible(document.visibilityState === 'visible');
  };

  useLayoutEffect(() => {
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, []);

  return isVisible;
}
